<template>
  <v-app id="embedded-upload">
    <v-main aria-live="polite" class="d-flex align-center">
      <div v-if="showingLoadingCard" class="d-flex flex-column justify-center align-center w-100">
        <v-progress-circular
          :size="80"
          :width="10"
          indeterminate
          color="primary"
          :aria-label="$t('common.loading')"
        />
      </div>

      <v-alert v-if="showingErrorCard" type="error" class="mx-4 pa-4">
        {{ linkError }}
      </v-alert>

      <v-card v-if="showingEmailCard" flat class="w-100">
        <v-container>
          <v-form ref="from" v-on:submit.prevent="submitNames" aria-label="TubeWay user details">

            <v-row>
              <v-col>
                <v-text-field
                  v-model="fullname"
                  :error-messages="fullnameErrors"
                  label="Name"
                  aria-label="Name"
                  hide-details="auto"
                  variant="outlined"
                  density="default"
                  color="primary"
                  required block flat
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-text-field
                  v-model="email"
                  :error-messages="emailErrors"
                  label="Email"
                  aria-label="Email"
                  hide-details="auto"
                  variant="outlined"
                  density="default"
                  color="primary"
                  required block flat
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-textarea
                  v-model="fileNotes"
                  label="File notes"
                  aria-label="File notes"
                  rows="2"
                  variant="outlined"
                  density="compact"
                  color="primary"
                  block hide-details flat
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-checkbox
                  v-model="acceptTerms"
                  label="Accept Terms and Conditions"
                  :error-messages="acceptTermsErrors"
                  class="mt-0"
                  color="primary"
                  hide-details="auto"
                  density="compact"
                  block
                >
                  <template v-slot:label>
                    <span>
                      <!-- Accept <a htef='https://www.example.com/' tabindex="0" target="_blank" role="link">Terms and Conditions</a> -->
                      Accept Terms and Conditions
                    </span>
                  </template>
                </v-checkbox>
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-btn
                type="submit"
                class="focusable-item"
                label="Next"
                color="primary"
                elevation="0"
                block
                >
                  Next
                </v-btn>
              </v-col>
            </v-row>

          </v-form>
        </v-container>
      </v-card>

      <EmbeddedUploadFiles
        v-if="showingUploadCard"
        :fullname="fullname"
        :email="email"
        :notes="fileNotes"
        @files-uploaded="filesUploadedEvent()"
      />

      <v-card v-if="showingThankYouCard" class="w-100">
        <v-container>
          <v-row>
            <v-col>
              <v-alert id="refThankYouMessage" type="success" class="py-4 focusable-item" tabindex="-1">
                Thank you for your submission!
              </v-alert>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-btn color="primary" class="focusable-item" block @click="closeThankYouForm()" tabindex="8001">
                Close
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-card>

    </v-main>

    <v-footer app>
      <v-row no-gutters>
        <v-col class="d-flex justify-center">
          Powered by <a href="http://portal.tubeway.io/" target="_blank">TubeWay<sub>.io</sub></a>
        </v-col>
      </v-row>
    </v-footer>
  </v-app>
</template>

<script>
import { useUploadLinkStore } from '@/stores/UploadLinkStore.js'
import { useTheme } from 'vuetify';
import Helper from '@/components/helper';
import EmbeddedUploadFiles from './components/EmbeddedUploadFiles.vue';

export default {
  name: "EmbeddedUploadIndex",
  components: {
    EmbeddedUploadFiles,
  },
  data() {
    return {
      fullname: '',
      fullnameErrors: [],
      fullnameRules: [
        ...Helper.getNameValidationRules(this),
        // v => v.length <= 30 || 'Name must be less than 30 characters',
      ],

      email: '',
      emailErrors: [],
      emailRules: Helper.getEmailValidationRules(this),

      fileNotes: '',
      acceptTerms: false,
      acceptTermsErrors: [],

      loadingLink: true,
      linkError: '',
      showUploadForm: false,
      showingThankYouForm: false,

    };
  },
  mounted() {
    const theme = useTheme();

    theme.themes.value.dark.colors.error = '#fb6969';
    theme.themes.value.light.colors.primary = '#003349';

    this.code = this.$route.params.code;

    const uploadLinkStore = useUploadLinkStore();
    uploadLinkStore.refreshUploadlinkInfo(this.code).then((info) => {
      if (info?.brand?.color) {
        this.$vuetify.theme.themes.dark.colors.primary = info.brand.color;
        this.$vuetify.theme.themes.light.colors.primary = info.brand.color;

        this.$vuetify.theme.themes.dark.colors['on-list'] = info.brand.color;
        this.$vuetify.theme.themes.light.colors['on-list'] = info.brand.color;
      }

      if (info?.brand?.backgroundColor) {
        this.$vuetify.theme.themes.dark.colors.background = info.brand.backgroundColor;
        this.$vuetify.theme.themes.light.colors.background = info.brand.backgroundColor;
      }

      if (info?.brand?.foregroundColor) {
        this.$vuetify.theme.themes.dark.colors['tw-surface'] = info.brand.foregroundColor;
        this.$vuetify.theme.themes.light.colors['tw-surface'] = info.brand.foregroundColor;

        this.$vuetify.theme.themes.dark.colors['surface'] = info.brand.foregroundColor;
        this.$vuetify.theme.themes.light.colors['surface'] = info.brand.foregroundColor;
      }

      if (info?.brand?.foregroundAccentColor) {
        this.$vuetify.theme.themes.dark.colors['tw-surface-accent'] = info.brand.foregroundAccentColor;
        this.$vuetify.theme.themes.light.colors['tw-surface-accent'] = info.brand.foregroundAccentColor;

        this.$vuetify.theme.themes.dark.colors['list'] = info.brand.foregroundAccentColor;
        this.$vuetify.theme.themes.light.colors['list'] = info.brand.foregroundAccentColor;
      }

      if (info?.brand?.theme && info?.brand?.theme === 'light') {
        theme.change('light');
      } else {
        theme.change('dark');
      }

      if (info?.params?.embeddable !== 1) {
        this.linkError = 'Invalid widget parameters!';
      }
    }).catch((error) => {
      if (error &&
        error.response &&
        error.response.data &&
        error.response.data.error &&
        error.response.data.error.message
      ) {
        this.linkError = error.response.data.error.message;
      } else {
        this.linkError = 'Error!';
      }
    }).finally(() => {
      this.loadingLink = false;
    });
  },
  methods: {
    validateNamesForm() {
      this.fullnameErrors = [];
      for (const validationFunction of this.fullnameRules) {
        const error = validationFunction(this.fullname);
        if (error !== true) {
          this.fullnameErrors.push(error);
        }
      }

      this.emailErrors = [];
      for (const validationFunction of this.emailRules) {
        const error = validationFunction(this.email);
        if (error !== true) {
          this.emailErrors.push(error);
        }
      }

      this.acceptTermsErrors = [];
      if (!this.acceptTerms) {
        this.acceptTermsErrors.push('You must accept the terms and conditions');
      }

      if (this.fullnameErrors.length > 0 ||
          this.emailErrors.length > 0 ||
          this.acceptTermsErrors.length > 0) {
        return false;
      }

      return true;
    },
    submitNames() {
      if (!this.validateNamesForm() || !this.$refs.from.validate()) {
        return;
      }

      this.showUploadForm = true;
    },
    filesUploadedEvent() {
      this.showingThankYouForm = true;

      this.$nextTick(() => {
        document.getElementById('refThankYouMessage').focus();
      });
    },
    closeThankYouForm() {
      this.showingThankYouForm = false
    },
  },
  computed: {
    showingLoadingCard() {
      return this.loadingLink;
    },
    showingErrorCard() {
      return !this.loadingLink && this.linkError;
    },
    showingEmailCard() {
      return !this.loadingLink && !this.linkError && !this.showUploadForm;
    },
    showingUploadCard() {
      return !this.loadingLink && !this.linkError && this.showUploadForm && !this.showingThankYouForm;
    },
    showingThankYouCard() {
      return !this.loadingLink && !this.linkError && this.showUploadForm && this.showingThankYouForm;
    },
  }
}
</script>

<style lang="scss" scoped>
#embedded-upload {
  .v-messages__message {
    font-size: 14px;
    font-weight: 400;
  }

  main {
    padding-bottom: 30px;
  }

  .focusable-item:focus {
    outline-width: 1px;
    outline-style: auto;
    outline-offset: 3px;
  }

  &.v-theme--dark {
    a {
      color: rgb(var(--v-theme-primary));
    }

    .v-messages__message {
      color: #fb6969;
    }

    .v-label {
      color: #ffffff;
    }
  }

  &.v-theme--light {
    a {
      color: #9F2E2E; /* Error Messages Red on a request link as embeddable widget */
    }

    .v-btn.primary {
      color: var(--tw-fg-color-primary) !important;
    }
  }
}
</style>
